import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import React, { useEffect } from "react";
import { v4 as uuid } from "uuid";
import { trackUser } from "../../analytics/trackUser";
import { useAuth } from "../../auth/useAuth";
import { DialogTakeover } from "../../components/Dialog";
import Loader from "../../components/Loader";
import { USER_COLORS } from "../../css/color";
import { currentUserAtom } from "../../jotai/atoms";
import { User } from "../../types";
import { randomItem } from "../../utils/randomItem";
import { NameForm } from "./EditUser";

const anonymousAtom = atomWithStorage<User | null>("anonymous", null);

export default function Onboarding({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentUser, setCurrentUser] = useAtom(currentUserAtom);
  const [anonymous, setAnonymous] = useAtom(anonymousAtom);
  const { user, hasFetched, update } = useAuth();

  useEffect(() => {
    let cur: User;

    if (hasFetched) {
      if (user) {
        cur = {
          color: randomItem(USER_COLORS),
          ...user,
          name: user.name || "",
        };
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const name = urlParams.get("name") ?? "";

        cur = anonymous
          ? anonymous
          : {
              id: uuid(),
              name,
              color: randomItem(USER_COLORS),
              anonymous: true,
            };

        setAnonymous(cur);
      }

      setCurrentUser(cur);
      trackUser(cur);
    }
  }, [user, setCurrentUser, hasFetched, anonymous, setAnonymous]);

  function setName(name: string) {
    if (!currentUser) return;

    setCurrentUser({ ...currentUser, name });

    if (user) {
      update({ name });
    } else {
      setAnonymous({ ...currentUser, name });
    }
  }

  if (!currentUser) {
    return <Loader />;
  }

  if (currentUser.name === "") {
    return (
      <DialogTakeover
        title="What's your name?"
        message="Enter a name or handle so people know how you’d like to be called. Your name will only be visible to others in spaces you joined."
      >
        <NameForm callback={setName} label="Enter" />
      </DialogTakeover>
    );
  }

  return <>{children}</>;
}
